import type {
  CartSummary,
  OrderMutationConflict,
} from "./order-line-controller";
import type {
  PricePreview,
  ProductionEstimate,
  PvcQuoteDomainRejection,
  QuoteGatewayResult,
} from "./pricing-controller";
import {
  AdapterContractError,
  type AddPvcInput,
  type GraphqlBinding,
  type PvcOrderBindings,
  type PvcQuoteBinding,
  type UpdatePvcInput,
  type WireGatewayMutationResult,
  type WireMutationResolution,
  type WireOrderLineResult,
} from "./pvc-shop-api-adapters";

const QUOTE_DOCUMENT = `
  query HappyPrintQuotePvcConfiguration($input: PvcQuoteInput!) {
    quotePvcConfiguration(input: $input) {
      __typename
      ... on PvcQuoteSuccess {
        quote {
          clientRevision
          requestFingerprint
          configurationHash
          pricingVersion
          quoteExpiresAt
          currencyCode
          subtotalMinor
          taxMinor
          totalMinor
          includesVat
          unitPriceMinor
          pricePerSqmMinor
          areaPerUnitSqm
          totalAreaSqm
          minChargeApplied
          minimumChargeMinor
          productionEstimate {
            earliestReadyDate
            selectedDateAvailable
            rush {
              available
              addonId
              surchargeMinor
              earliestReadyDate
            }
          }
        }
      }
      ... on PvcQuoteRejection {
        clientRevision
        requestFingerprint
        code
        fieldErrors { field code }
        invalidOptionIds
        earliestAvailableDate
        currentSchemaVersion
      }
    }
  }
`;

const MUTATION_RESULT_FIELDS = `
  __typename
  ... on PvcOrderMutationSuccess {
    clientMutationId
    orderLineId
    orderLineVersion
    cart {
      id
      totalQuantity
      totalWithTax
      currencyCode
      addedOrUpdatedLine {
        id
        quantity
        productName
        totalWithTax
      }
    }
  }
  ... on PvcOrderMutationConflict {
    clientMutationId
    code
    fieldErrors { field code }
    invalidOptionIds
    earliestAvailableDate
    currentSchemaVersion
    currentVersion
    guestCart { id totalQuantity totalWithTax currencyCode }
    accountCart { id totalQuantity totalWithTax currencyCode }
  }
`;

const ADD_DOCUMENT = `
  mutation HappyPrintAddPvcSignToOrder($input: PvcAddToOrderInput!) {
    addPvcSignToOrder(input: $input) {
      ${MUTATION_RESULT_FIELDS}
    }
  }
`;

const UPDATE_DOCUMENT = `
  mutation HappyPrintUpdatePvcOrderLine($input: PvcUpdateOrderLineInput!) {
    updatePvcOrderLine(input: $input) {
      ${MUTATION_RESULT_FIELDS}
    }
  }
`;

const RESOLVE_MUTATION_DOCUMENT = `
  query HappyPrintResolvePvcMutation($clientMutationId: String!) {
    resolvePvcMutation(clientMutationId: $clientMutationId) {
      clientMutationId
      status
      result {
        orderLineId
        orderLineVersion
        cart {
          id
          totalQuantity
          totalWithTax
          currencyCode
          addedOrUpdatedLine {
            id
            quantity
            productName
            totalWithTax
          }
        }
      }
    }
  }
`;

const RECOVER_CART_SESSION_DOCUMENT = `
  mutation HappyPrintRecoverPvcCartSession {
    recoverPvcCartSession { ok cartId }
  }
`;

const RESOLVE_CART_OWNERSHIP_DOCUMENT = `
  mutation HappyPrintResolvePvcCartOwnership($choice: String!) {
    resolvePvcCartOwnership(choice: $choice) { ok activeCartId }
  }
`;

export type HappyPrintLoginInput = {
  username: string;
  password: string;
  rememberMe: boolean;
};

export type HappyPrintLoginResult =
  | { type: "success"; userId: string }
  | { type: "failure"; code: string }
  | {
      type: "cart-ownership-conflict";
      code: "CART_OWNERSHIP_CONFLICT";
      guestCart: CartSummary;
      accountCart: CartSummary;
    };

const LOGIN_DOCUMENT = `
  mutation HappyPrintLogin($input: HappyPrintLoginInput!) {
    happyPrintLogin(input: $input) {
      __typename
      ... on HappyPrintLoginSuccess { ok userId }
      ... on HappyPrintLoginFailure { ok code }
      ... on HappyPrintCartOwnershipConflict {
        code
        guestCart { id totalQuantity totalWithTax currencyCode }
        accountCart { id totalQuantity totalWithTax currencyCode }
      }
    }
  }
`;

function fail(message: string, details?: unknown): never {
  throw new AdapterContractError(message, details);
}

function record(value: unknown, path: string): Record<string, unknown> {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    fail(`${path} must be an object`, value);
  }
  return value as Record<string, unknown>;
}

function stringValue(value: unknown, path: string): string {
  if (typeof value !== "string") fail(`${path} must be a string`, value);
  return value;
}

function nonEmptyString(value: unknown, path: string): string {
  const result = stringValue(value, path);
  if (result.length === 0) fail(`${path} must not be empty`, value);
  return result;
}

function numberValue(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    fail(`${path} must be a finite number`, value);
  }
  return value;
}

function integerValue(value: unknown, path: string): number {
  const result = numberValue(value, path);
  if (!Number.isInteger(result)) fail(`${path} must be an integer`, value);
  return result;
}

function booleanValue(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") fail(`${path} must be a boolean`, value);
  return value;
}

function nullableString(value: unknown, path: string): string | null {
  return value == null ? null : stringValue(value, path);
}

function nullableInteger(value: unknown, path: string): number | null {
  return value == null ? null : integerValue(value, path);
}

function nullableBoolean(value: unknown, path: string): boolean | null {
  return value == null ? null : booleanValue(value, path);
}

function stringArray(value: unknown, path: string): string[] {
  if (!Array.isArray(value)) fail(`${path} must be an array`, value);
  return value.map((item, index) => stringValue(item, `${path}[${index}]`));
}

function fieldErrors(value: unknown, path: string): Array<{ field: string; code: string }> {
  if (!Array.isArray(value)) fail(`${path} must be an array`, value);
  return value.map((item, index) => {
    const entry = record(item, `${path}[${index}]`);
    return {
      field: nonEmptyString(entry.field, `${path}[${index}].field`),
      code: nonEmptyString(entry.code, `${path}[${index}].code`),
    };
  });
}

function root(data: unknown, field: string): Record<string, unknown> {
  const envelope = record(data, "data");
  return record(envelope[field], `data.${field}`);
}

function cartSummary(value: unknown, path: string): CartSummary {
  const item = record(value, path);
  return {
    id: nonEmptyString(item.id, `${path}.id`),
    totalQuantity: integerValue(item.totalQuantity, `${path}.totalQuantity`),
    totalWithTax: integerValue(item.totalWithTax, `${path}.totalWithTax`),
    currencyCode: nonEmptyString(item.currencyCode, `${path}.currencyCode`),
  };
}

function orderLineResult(value: unknown, path: string): WireOrderLineResult {
  const item = record(value, path);
  const cart = record(item.cart, `${path}.cart`);
  const line = record(cart.addedOrUpdatedLine, `${path}.cart.addedOrUpdatedLine`);
  return {
    orderLineId: nonEmptyString(item.orderLineId, `${path}.orderLineId`),
    orderLineVersion: integerValue(item.orderLineVersion, `${path}.orderLineVersion`),
    cart: {
      ...cartSummary(cart, `${path}.cart`),
      addedOrUpdatedLine: {
        id: nonEmptyString(line.id, `${path}.cart.addedOrUpdatedLine.id`),
        quantity: integerValue(line.quantity, `${path}.cart.addedOrUpdatedLine.quantity`),
        productName: stringValue(line.productName, `${path}.cart.addedOrUpdatedLine.productName`),
        totalWithTax: integerValue(line.totalWithTax, `${path}.cart.addedOrUpdatedLine.totalWithTax`),
      },
    },
  };
}

function productionEstimate(value: unknown, path: string): ProductionEstimate {
  const item = record(value, path);
  const result: ProductionEstimate = {
    earliestReadyDate: nonEmptyString(item.earliestReadyDate, `${path}.earliestReadyDate`),
    selectedDateAvailable: nullableBoolean(item.selectedDateAvailable, `${path}.selectedDateAvailable`),
  };
  if (item.rush != null) {
    const rush = record(item.rush, `${path}.rush`);
    result.rush = {
      available: booleanValue(rush.available, `${path}.rush.available`),
      addonId: nullableString(rush.addonId, `${path}.rush.addonId`),
      surchargeMinor: nullableInteger(rush.surchargeMinor, `${path}.rush.surchargeMinor`),
      earliestReadyDate: nullableString(rush.earliestReadyDate, `${path}.rush.earliestReadyDate`),
    };
  }
  return result;
}

function pricePreview(value: unknown, path: string): PricePreview {
  const quote = record(value, path);
  const currencyCode = stringValue(quote.currencyCode, `${path}.currencyCode`);
  if (currencyCode !== "ILS") fail(`${path}.currencyCode must be ILS`, currencyCode);
  return {
    clientRevision: integerValue(quote.clientRevision, `${path}.clientRevision`),
    requestFingerprint: stringValue(quote.requestFingerprint, `${path}.requestFingerprint`),
    configurationHash: nonEmptyString(quote.configurationHash, `${path}.configurationHash`),
    pricingVersion: nonEmptyString(quote.pricingVersion, `${path}.pricingVersion`),
    quoteExpiresAt: nonEmptyString(quote.quoteExpiresAt, `${path}.quoteExpiresAt`),
    currencyCode: "ILS",
    subtotalMinor: integerValue(quote.subtotalMinor, `${path}.subtotalMinor`),
    taxMinor: integerValue(quote.taxMinor, `${path}.taxMinor`),
    totalMinor: integerValue(quote.totalMinor, `${path}.totalMinor`),
    includesVat: booleanValue(quote.includesVat, `${path}.includesVat`),
    unitPriceMinor: integerValue(quote.unitPriceMinor, `${path}.unitPriceMinor`),
    pricePerSqmMinor: nullableInteger(quote.pricePerSqmMinor, `${path}.pricePerSqmMinor`),
    areaPerUnitSqm: numberValue(quote.areaPerUnitSqm, `${path}.areaPerUnitSqm`),
    totalAreaSqm: numberValue(quote.totalAreaSqm, `${path}.totalAreaSqm`),
    minChargeApplied: booleanValue(quote.minChargeApplied, `${path}.minChargeApplied`),
    minimumChargeMinor: integerValue(quote.minimumChargeMinor, `${path}.minimumChargeMinor`),
    productionEstimate: productionEstimate(quote.productionEstimate, `${path}.productionEstimate`),
  };
}

function quoteRejection(value: Record<string, unknown>, path: string): PvcQuoteDomainRejection {
  const code = stringValue(value.code, `${path}.code`);
  if (
    code !== "CONFIGURATION_INVALID" &&
    code !== "PRODUCT_SCHEMA_CHANGED" &&
    code !== "OPTION_UNAVAILABLE" &&
    code !== "DATE_UNAVAILABLE"
  ) {
    fail(`${path}.code is not a supported PVC quote rejection`, code);
  }
  return {
    clientRevision: integerValue(value.clientRevision, `${path}.clientRevision`),
    requestFingerprint: stringValue(value.requestFingerprint, `${path}.requestFingerprint`),
    code,
    fieldErrors: fieldErrors(value.fieldErrors, `${path}.fieldErrors`),
    invalidOptionIds: stringArray(value.invalidOptionIds, `${path}.invalidOptionIds`),
    earliestAvailableDate: nullableString(value.earliestAvailableDate, `${path}.earliestAvailableDate`),
    currentSchemaVersion: nullableInteger(value.currentSchemaVersion, `${path}.currentSchemaVersion`),
  };
}

function decodeQuote(data: unknown): QuoteGatewayResult {
  const result = root(data, "quotePvcConfiguration");
  const typename = stringValue(result.__typename, "data.quotePvcConfiguration.__typename");
  if (typename === "PvcQuoteSuccess") {
    return { type: "success", quote: pricePreview(result.quote, "data.quotePvcConfiguration.quote") };
  }
  if (typename === "PvcQuoteRejection") {
    return { type: "rejected", rejection: quoteRejection(result, "data.quotePvcConfiguration") };
  }
  return fail("quotePvcConfiguration returned an unknown concrete type", typename);
}

function conflict(value: Record<string, unknown>, path: string): OrderMutationConflict {
  const code = stringValue(value.code, `${path}.code`);
  switch (code) {
    case "CONFIGURATION_INVALID":
      return { code, fieldErrors: fieldErrors(value.fieldErrors, `${path}.fieldErrors`) };
    case "PRODUCT_SCHEMA_CHANGED": {
      const currentSchemaVersion = nullableInteger(value.currentSchemaVersion, `${path}.currentSchemaVersion`);
      if (currentSchemaVersion == null) fail(`${path}.currentSchemaVersion is required for ${code}`);
      return { code, currentSchemaVersion };
    }
    case "OPTION_UNAVAILABLE":
      return { code, invalidOptionIds: stringArray(value.invalidOptionIds, `${path}.invalidOptionIds`) };
    case "ARTWORK_FILE_INVALID":
    case "PRICE_CHANGED":
    case "CART_SESSION_EXPIRED":
      return { code };
    case "DATE_UNAVAILABLE":
      return { code, earliestAvailableDate: nullableString(value.earliestAvailableDate, `${path}.earliestAvailableDate`) };
    case "CART_OWNERSHIP_CONFLICT":
      return {
        code,
        guestCart: cartSummary(value.guestCart, `${path}.guestCart`),
        accountCart: cartSummary(value.accountCart, `${path}.accountCart`),
      };
    case "ORDER_LINE_VERSION_CONFLICT": {
      const currentVersion = nullableInteger(value.currentVersion, `${path}.currentVersion`);
      if (currentVersion == null) fail(`${path}.currentVersion is required for ${code}`);
      return { code, currentVersion };
    }
    default:
      return fail(`${path}.code is not a supported PVC mutation conflict`, code);
  }
}

function decodeMutationRoot(data: unknown, field: string): WireGatewayMutationResult {
  const result = root(data, field);
  const path = `data.${field}`;
  const typename = stringValue(result.__typename, `${path}.__typename`);
  const clientMutationId = nonEmptyString(result.clientMutationId, `${path}.clientMutationId`);
  if (typename === "PvcOrderMutationSuccess") {
    return {
      type: "success",
      clientMutationId,
      result: orderLineResult(result, path),
    };
  }
  if (typename === "PvcOrderMutationConflict") {
    return {
      type: "conflict",
      clientMutationId,
      conflict: conflict(result, path),
    };
  }
  return fail(`${field} returned an unknown concrete type`, typename);
}

function decodeResolution(data: unknown): WireMutationResolution {
  const result = root(data, "resolvePvcMutation");
  const clientMutationId = nonEmptyString(result.clientMutationId, "data.resolvePvcMutation.clientMutationId");
  const status = stringValue(result.status, "data.resolvePvcMutation.status");
  if (status === "committed") {
    if (result.result == null) fail("resolvePvcMutation committed result is missing");
    return {
      status,
      clientMutationId,
      result: orderLineResult(result.result, "data.resolvePvcMutation.result"),
    };
  }
  if (status === "pending" || status === "not-found") {
    return { status, clientMutationId };
  }
  return fail("resolvePvcMutation returned an unknown status", status);
}

function pvcAddInput(input: AddPvcInput) {
  return {
    clientMutationId: input.clientMutationId,
    configuration: input.configuration,
    schemaVersion: input.schemaVersion,
    artwork: input.artwork,
    quote: input.quote,
  };
}

function pvcUpdateInput(input: UpdatePvcInput) {
  return {
    ...pvcAddInput(input),
    orderLineId: input.orderLineId,
    expectedVersion: input.expectedVersion,
  };
}

export const pvcQuoteBinding: PvcQuoteBinding = {
  operationName: "HappyPrintQuotePvcConfiguration",
  document: QUOTE_DOCUMENT,
  variables: input => ({ input }),
  decode: decodeQuote,
};

export const pvcOrderBindings: PvcOrderBindings = {
  add: {
    operationName: "HappyPrintAddPvcSignToOrder",
    document: ADD_DOCUMENT,
    variables: input => ({ input: pvcAddInput(input) }),
    decode: data => decodeMutationRoot(data, "addPvcSignToOrder"),
  },
  update: {
    operationName: "HappyPrintUpdatePvcOrderLine",
    document: UPDATE_DOCUMENT,
    variables: input => ({ input: pvcUpdateInput(input) }),
    decode: data => decodeMutationRoot(data, "updatePvcOrderLine"),
  },
  resolveMutation: {
    operationName: "HappyPrintResolvePvcMutation",
    document: RESOLVE_MUTATION_DOCUMENT,
    variables: input => ({ clientMutationId: input.clientMutationId }),
    decode: decodeResolution,
  },
  recoverCartSession: {
    operationName: "HappyPrintRecoverPvcCartSession",
    document: RECOVER_CART_SESSION_DOCUMENT,
    variables: () => ({}),
    decode: data => {
      const result = root(data, "recoverPvcCartSession");
      if (booleanValue(result.ok, "data.recoverPvcCartSession.ok") !== true) {
        fail("recoverPvcCartSession returned ok=false", result);
      }
      nonEmptyString(result.cartId, "data.recoverPvcCartSession.cartId");
      return { ok: true };
    },
  },
  resolveCartOwnership: {
    operationName: "HappyPrintResolvePvcCartOwnership",
    document: RESOLVE_CART_OWNERSHIP_DOCUMENT,
    variables: input => ({ choice: input.choice }),
    decode: data => {
      const result = root(data, "resolvePvcCartOwnership");
      if (booleanValue(result.ok, "data.resolvePvcCartOwnership.ok") !== true) {
        fail("resolvePvcCartOwnership returned ok=false", result);
      }
      nonEmptyString(result.activeCartId, "data.resolvePvcCartOwnership.activeCartId");
      return { ok: true };
    },
  },
};

export const happyPrintLoginBinding: GraphqlBinding<HappyPrintLoginInput, HappyPrintLoginResult> = {
  operationName: "HappyPrintLogin",
  document: LOGIN_DOCUMENT,
  variables: input => ({ input: { ...input } }),
  decode: data => {
    const result = root(data, "happyPrintLogin");
    const typename = stringValue(result.__typename, "data.happyPrintLogin.__typename");
    if (typename === "HappyPrintLoginSuccess") {
      if (booleanValue(result.ok, "data.happyPrintLogin.ok") !== true) {
        fail("HappyPrintLoginSuccess returned ok=false", result);
      }
      return { type: "success", userId: nonEmptyString(result.userId, "data.happyPrintLogin.userId") };
    }
    if (typename === "HappyPrintLoginFailure") {
      if (booleanValue(result.ok, "data.happyPrintLogin.ok") !== false) {
        fail("HappyPrintLoginFailure returned ok=true", result);
      }
      return { type: "failure", code: nonEmptyString(result.code, "data.happyPrintLogin.code") };
    }
    if (typename === "HappyPrintCartOwnershipConflict") {
      const code = stringValue(result.code, "data.happyPrintLogin.code");
      if (code !== "CART_OWNERSHIP_CONFLICT") {
        fail("HappyPrint cart ownership conflict returned an unexpected code", code);
      }
      return {
        type: "cart-ownership-conflict",
        code,
        guestCart: cartSummary(result.guestCart, "data.happyPrintLogin.guestCart"),
        accountCart: cartSummary(result.accountCart, "data.happyPrintLogin.accountCart"),
      };
    }
    return fail("happyPrintLogin returned an unknown concrete type", typename);
  },
};
